import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus, Pencil, Trash2, Loader2, AlertTriangle, X, BookOpen, Calendar, CalendarDays, Building2, Globe, Lock, Info } from 'lucide-react';
import { listAgreements, createAgreement, updateAgreement, deleteAgreement, searchCompanies } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/Toast';
import { FEEDBACK } from '../services/feedbackMessages';
import {
  GLOBAL_COMPANY_VALUE,
  isSuperAdmin,
  isGlobalAgreement,
  hasCompanyField,
  listHasCompanyScope,
  canCreateAgreements,
  canEditAgreement,
  canDeleteAgreement,
  canChangeAgreementCompany,
  visibleAgreementsForUser,
  emptyAgreementForm,
  agreementToForm,
  validateAgreementForm,
  buildAgreementCreatePayload,
  buildAgreementUpdatePayload,
} from '../utils/agreements';

// Tipos de vacaciones (backend lo recibe como string enum):
//   'laborables' → solo cuentan días laborables del trabajador (excluye findes,
//      festivos y días de descanso).
//   'festivos'   → vacaciones naturales: cuentan TODOS los días del rango.
const HOLIDAY_TYPES = [
  { value: 'laborables', label: 'Laborables', hint: 'Excluye findes, festivos y días de descanso del trabajador.' },
  { value: 'festivos',   label: 'Naturales',  hint: 'Cuentan todos los días: incluye findes y festivos.' },
];

const AgreementModal = ({ open, initial, user, companies, companyName, onClose, onSubmit }) => {
  const isEdit = !!initial?.id;
  const superAdmin = isSuperAdmin(user);
  const canPickCompany = canChangeAgreementCompany(user);
  const [form, setForm] = useState(() => emptyAgreementForm(user));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Dependemos de type/companyId (primitivos) y no del objeto user: si el
  // contexto recrea el objeto, no queremos resetear el formulario a medias.
  const userType = user?.type;
  const userCompanyId = user?.companyId;
  useEffect(() => {
    if (open) {
      setForm(initial
        ? agreementToForm(initial)
        : emptyAgreementForm({ type: userType, companyId: userCompanyId }));
      setError(null);
    }
  }, [open, initial, userType, userCompanyId]);

  if (!open) return null;

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    setError(null);
    const validation = validateAgreementForm(form, user, { isEdit });
    if (validation) { setError(validation); return; }
    setSubmitting(true);
    try {
      await onSubmit(isEdit
        ? buildAgreementUpdatePayload(form, initial, user)
        : buildAgreementCreatePayload(form, user));
      onClose();
    } catch (err) {
      setError(err.message || 'Error al guardar.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 backdrop-blur-sm p-4">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold">{isEdit ? 'Editar' : 'Alta'}</div>
            <h2 className="font-display text-lg font-bold text-ink">{isEdit ? 'Editar convenio' : 'Nuevo convenio'}</h2>
          </div>
          <button onClick={onClose} className="text-ink-muted hover:text-ink p-1 rounded-lg hover:bg-slate-100 transition-colors">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">Empresa propietaria</label>
            {canPickCompany ? (
              <>
                <select
                  value={form.companyId}
                  onChange={(e) => setForm((f) => ({ ...f, companyId: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:border-primary"
                >
                  <option value="">— Selecciona empresa —</option>
                  <option value={GLOBAL_COMPANY_VALUE}>Global (todas las empresas)</option>
                  {companies.map((c) => (
                    <option key={c.id} value={String(c.id)}>{c.name}</option>
                  ))}
                </select>
                <div className="text-[11px] text-ink-muted mt-1.5 leading-snug">
                  {form.companyId === GLOBAL_COMPANY_VALUE
                    ? 'Convenio global: lo ven y lo pueden asignar todas las empresas, pero solo el superadmin puede editarlo.'
                    : 'Solo esta empresa verá el convenio y podrá asignarlo a sus trabajadores.'}
                </div>
              </>
            ) : (
              <>
                <div className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50 text-ink inline-flex items-center gap-2">
                  <Building2 size={14} className="text-ink-muted shrink-0" />
                  <span className="truncate">{companyName || 'Tu empresa'}</span>
                </div>
                <div className="text-[11px] text-ink-muted mt-1.5 leading-snug">
                  El convenio se crea siempre en tu empresa. Los convenios globales solo los gestiona el superadmin.
                </div>
              </>
            )}
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">Nombre del convenio</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-primary"
              placeholder="Ej. Oficinas y despachos - Sevilla"
              autoFocus
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">Días vacaciones / año</label>
              <input
                type="number"
                value={form.vacationDays}
                onChange={(e) => setForm((f) => ({ ...f, vacationDays: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-primary"
                min="0" max="365" step="1"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">Horas / mes</label>
              <input
                type="number"
                value={form.monthlyHours}
                onChange={(e) => setForm((f) => ({ ...f, monthlyHours: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-primary"
                min="1" max="744" step="1"
                placeholder={isEdit ? 'Sin cambiar' : ''}
              />
            </div>
          </div>
          <div className="text-[11px] text-ink-muted -mt-2 leading-snug">
            Horas de contrato al mes que fija el convenio. La jornada diaria de cada trabajador sale de su horario.
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">Tipo de vacaciones</label>
            <div className="grid grid-cols-2 gap-2">
              {HOLIDAY_TYPES.map((opt) => {
                const active = form.holidayType === opt.value;
                const Icon = opt.value === 'festivos' ? CalendarDays : Calendar;
                return (
                  <button
                    type="button"
                    key={opt.value}
                    onClick={() => setForm((f) => ({ ...f, holidayType: opt.value }))}
                    className={`text-left p-3 rounded-xl border transition-colors ${
                      active
                        ? 'border-primary bg-primary-soft/40'
                        : 'border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Icon size={14} className={active ? 'text-primary' : 'text-ink-muted'} />
                      <span className={`text-sm font-semibold ${active ? 'text-primary-darker' : 'text-ink'}`}>{opt.label}</span>
                    </div>
                    <div className="text-[11px] text-ink-muted leading-snug">{opt.hint}</div>
                  </button>
                );
              })}
            </div>
          </div>
          {superAdmin && isEdit && !hasCompanyField(initial) && (
            <div className="text-[11px] text-ink-muted bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 leading-snug">
              El servidor todavía no guarda la empresa de los convenios: el cambio de empresa se aplicará cuando esté actualizado.
            </div>
          )}
          {error && (
            <div className="text-xs text-danger bg-danger/5 border border-danger/20 rounded-lg px-3 py-2">{error}</div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} disabled={submitting} className="px-4 py-2 rounded-lg text-sm font-semibold text-ink-muted hover:bg-slate-100 transition-colors disabled:opacity-50">
              Cancelar
            </button>
            <button type="submit" disabled={submitting} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-primary text-white hover:bg-primary-dark transition-colors disabled:opacity-60">
              {submitting && <Loader2 size={14} className="animate-spin" />}
              {isEdit ? 'Guardar cambios' : 'Crear convenio'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ConveniosPage = () => {
  const { user } = useAuth();
  const [rawItems, setRawItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalInitial, setModalInitial] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const toast = useToast();

  const superAdmin = isSuperAdmin(user);
  const canCreate = canCreateAgreements(user);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Empresas para el selector del alta (superadmin) y para poner nombre al
      // companyId de cada fila. Para admin empresa/sede basta con la suya.
      const [data, comps] = await Promise.all([
        listAgreements(),
        searchCompanies('').catch(() => []),
      ]);
      setRawItems(Array.isArray(data) ? data : []);
      setCompanies(Array.isArray(comps) ? comps : []);
    } catch (err) {
      setError(err);
      setRawItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // El backend nuevo ya acota el GET por rol; filtramos también aquí por si
  // responde el antiguo (que devuelve los convenios de todas las empresas).
  const items = useMemo(() => visibleAgreementsForUser(rawItems, user), [rawItems, user]);

  // Backend antiguo: ningún convenio trae companyId. Para admin empresa/sede y
  // supervisor no podemos saber cuáles son suyos → avisamos en vez de listar
  // (y no dejamos crear: el convenio saldría sin dueño, visible para todos).
  const legacyBackend = rawItems.length > 0 && !listHasCompanyScope(rawItems);
  const scopedLegacy = legacyBackend && !superAdmin && user?.type !== 4;
  const showCreate = canCreate && !scopedLegacy;

  const companyNameById = useMemo(() => {
    const m = new Map();
    for (const c of companies) m.set(Number(c.id), c.name);
    return m;
  }, [companies]);

  const ownCompanyName = user?.companyId != null ? companyNameById.get(Number(user.companyId)) : null;

  const globalCount = useMemo(() => items.filter(isGlobalAgreement).length, [items]);

  const openCreate = () => { setModalInitial(null); setModalOpen(true); };
  const openEdit = (item) => { setModalInitial(item); setModalOpen(true); };

  // Errores del alta/edición: el modal los enseña (sigue abierto).
  const handleSubmit = async (payload) => {
    const isEdit = !!modalInitial?.id;
    if (isEdit) {
      await updateAgreement(modalInitial.id, payload);
    } else {
      await createAgreement(payload);
    }
    toast.success(FEEDBACK.agreementSaved({ name: payload?.name || modalInitial?.name, created: !isEdit }));
    await load();
  };

  const handleDelete = async (item) => {
    const extra = isGlobalAgreement(item)
      ? ' Es un convenio GLOBAL: afecta a los trabajadores de todas las empresas que lo tengan.'
      : '';
    if (!window.confirm(`¿Borrar el convenio "${item.name}"? Los trabajadores con este convenio se quedarán sin él.${extra}`)) return;
    try {
      await deleteAgreement(item.id);
      toast.success(FEEDBACK.agreementDeleted(item.name));
      await load();
    } catch (err) {
      toast.error(err, 'No se pudo borrar el convenio.');
    }
  };

  const companyCell = (a) => {
    if (isGlobalAgreement(a)) {
      return (
        <span
          title="Convenio global: disponible para todas las empresas. Solo el superadmin puede editarlo."
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-primary-soft text-primary-darker"
        >
          <Globe size={11} /> Global
        </span>
      );
    }
    if (!hasCompanyField(a) || a.companyId == null) {
      return <span className="text-ink-muted text-[13px]">—</span>;
    }
    return (
      <span className="inline-flex items-center gap-1.5 text-ink-muted text-[13px]">
        <Building2 size={12} className="shrink-0" />
        {companyNameById.get(Number(a.companyId)) || `Empresa #${a.companyId}`}
      </span>
    );
  };

  const subtitle = superAdmin
    ? 'Define los convenios asignables a cada trabajador (días de vacaciones, horas y tipo de cómputo). Pueden ser de una empresa o globales para todas.'
    : canCreate
      ? 'Convenios de tu empresa y convenios globales. Puedes crear y editar los de tu empresa; los globales son de solo lectura.'
      : 'Convenios disponibles (solo lectura).';

  return (
    <div className="h-[calc(100vh-64px-4rem)] flex flex-col min-h-0">
      <div className="flex items-end justify-between mb-5 gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold mb-1">Configuración</div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-ink">Convenios colectivos</h1>
          <div className="text-xs text-ink-muted mt-1">{subtitle}</div>
        </div>
        {showCreate && (
          <button
            onClick={openCreate}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-primary text-white hover:bg-primary-dark transition-colors shrink-0"
          >
            <Plus size={16} /> Nuevo convenio
          </button>
        )}
      </div>

      {error && (
        <div className="border border-danger/30 bg-danger/5 rounded-xl p-3 mb-4 flex items-start gap-3">
          <AlertTriangle size={16} className="text-danger shrink-0 mt-0.5" />
          <div className="flex-1 text-sm font-semibold text-ink">{error.message}</div>
        </div>
      )}

      {!loading && scopedLegacy && (
        <div className="border border-warning/30 bg-warning/5 rounded-xl p-3 mb-4 flex items-start gap-3">
          <Info size={16} className="text-warning shrink-0 mt-0.5" />
          <div className="flex-1 text-sm text-ink">
            La gestión de convenios por empresa estará disponible en cuanto se actualice el servidor.
            Mientras tanto, los convenios los gestiona el superadmin.
          </div>
        </div>
      )}

      {!loading && !scopedLegacy && !superAdmin && globalCount > 0 && (
        <div className="border border-slate-200 bg-white rounded-xl p-3 mb-4 flex items-start gap-3">
          <Lock size={16} className="text-ink-muted shrink-0 mt-0.5" />
          <div className="flex-1 text-xs text-ink-muted">
            Los convenios marcados como <span className="font-semibold text-ink">Global</span> son comunes a todas las
            empresas: puedes asignarlos a tus trabajadores, pero solo el superadmin puede modificarlos.
          </div>
        </div>
      )}

      <div className="border-t border-slate-200 flex-1 min-h-0 overflow-y-auto">
        {loading ? (
          <div className="py-10 flex items-center justify-center gap-2 text-ink-muted">
            <Loader2 size={18} className="animate-spin text-primary" />
            <span className="text-sm">Cargando convenios…</span>
          </div>
        ) : items.length === 0 ? (
          !scopedLegacy && (
            <div className="py-16 flex flex-col items-center gap-3 text-center">
              <BookOpen size={32} className="text-ink-muted/50" />
              <div className="text-sm text-ink-muted">No hay convenios registrados todavía.</div>
              {showCreate && (
                <button onClick={openCreate} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-semibold text-primary hover:bg-primary-soft transition-colors">
                  <Plus size={14} /> Crear el primero
                </button>
              )}
            </div>
          )
        ) : (
          <table className="w-full">
            <thead className="sticky top-0 bg-background">
              <tr className="text-xs uppercase tracking-wider text-ink-muted">
                <th className="text-left font-semibold py-3 pr-4">Nombre</th>
                <th className="text-left font-semibold py-3 pr-4">Empresa</th>
                <th className="text-right font-semibold py-3 pr-4">Días vacaciones</th>
                <th className="text-right font-semibold py-3 pr-4">Horas / mes</th>
                <th className="text-left font-semibold py-3 pr-4">Tipo</th>
                <th className="text-right font-semibold py-3 pr-4 w-32"></th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {items.map((a) => {
                const isNatural = a.holidayType === 'festivos';
                const editable = canEditAgreement(user, a);
                const deletable = canDeleteAgreement(user, a);
                return (
                <tr key={a.id} className="border-t border-slate-100 hover:bg-white transition-colors">
                  <td className="py-2.5 pr-4 font-semibold text-ink">{a.name}</td>
                  <td className="py-2.5 pr-4">{companyCell(a)}</td>
                  <td className="py-2.5 pr-4 text-right tabular-nums text-ink">{a.vacationDays}</td>
                  <td className="py-2.5 pr-4 text-right tabular-nums text-ink">{a.monthlyHours ?? '—'}</td>
                  <td className="py-2.5 pr-4">
                    <span
                      title={isNatural
                        ? 'Cuentan todos los días naturales del rango (findes y festivos incluidos).'
                        : 'Excluye findes, festivos y días de descanso del trabajador.'}
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                        isNatural
                          ? 'bg-warning/10 text-warning'
                          : 'bg-primary-soft text-primary-darker'
                      }`}
                    >
                      {isNatural ? <CalendarDays size={11} /> : <Calendar size={11} />}
                      {isNatural ? 'Naturales' : 'Laborables'}
                    </span>
                  </td>
                  <td className="py-2.5 pr-4 text-right">
                    {(editable || deletable) ? (
                      <div className="inline-flex items-center gap-1">
                        {editable && (
                          <button
                            onClick={() => openEdit(a)}
                            className="p-2 rounded-lg text-ink-muted hover:text-primary hover:bg-slate-100 transition-colors"
                            title="Editar"
                          >
                            <Pencil size={14} />
                          </button>
                        )}
                        {deletable && (
                          <button
                            onClick={() => handleDelete(a)}
                            className="p-2 rounded-lg text-ink-muted hover:text-danger hover:bg-slate-100 transition-colors"
                            title="Borrar"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    ) : (
                      <span
                        className="inline-flex items-center gap-1 text-[11px] text-ink-muted"
                        title={isGlobalAgreement(a) ? 'Convenio global: solo lo modifica el superadmin.' : 'Solo lectura.'}
                      >
                        <Lock size={11} /> Solo lectura
                      </span>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <AgreementModal
        open={modalOpen}
        initial={modalInitial}
        user={user}
        companies={companies}
        companyName={ownCompanyName}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
      />
    </div>
  );
};

export default ConveniosPage;
